'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from 'next/image';

import type { Reference } from '@eliadmin/core/src/api/references/references-types';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselProgress } from '@eliadmin/core/src/components/ui/carousel-progress';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { LuArrowUpRight } from 'react-icons/lu';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { lazy, Suspense, useState } from 'react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const LightboxComponent = lazy(() =>
  import('@eliadmin/core/src/components/lightbox/lightbox-component').then(module => ({
    default: module.LightboxComponent,
  })),
);

export function References({
  reviewsTitle,
  references,
}: {
  reviewsTitle: string;
  references: Reference[];
}) {
  const options = {
    align: 'center' as const,
    loop: true,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  return (
    <section className={cn('custom-section', 'px-0 py-6 sm:py-8 md:py-10 lg:py-12', 'generic-gap')}>
      <Heading title={reviewsTitle} center />
      <Carousel
        options={options}
        className={{
          wrapper: 'w-full',
          slide: 'mx-[6px] w-full max-w-[80%] sm:max-w-[78%] lg:max-w-[70%] xl:max-w-[65%]',
          container: 'w-full',
          main: 'w-full',
        }}
        slides={references.map(reference => (
          <ReferenceCard reference={reference} key={reference.name} />
        ))}
      />
      <div className="flex w-full max-w-[480px] items-center justify-center px-4">
        <CarouselProgress selectedIndex={selectedIndex} totalSlides={references.length} />
      </div>
      {/* <div className="flex w-full items-center justify-center px-4">
        <Button asChild variant={'outline'} className="w-full sm:w-fit">
          <Link prefetch={false} href={'/'}>
            Všetky referencie
          </Link>
        </Button>
      </div> */}
    </section>
  );
}

function ReferenceCard({ reference }: { reference: Reference }) {
  const { t } = useTranslations();
  const allImages = [reference?.image, ...(reference?.images ?? [])];

  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  return (
    <>
      {isOpen && (
        <Suspense fallback={null}>
          <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
        </Suspense>
      )}
      <div className="relative flex h-[450px] w-full flex-col items-start justify-between overflow-hidden rounded-[20px] p-6">
        <div className="relative z-10 flex w-full items-start justify-start gap-2">
          {!!reference.year && (
            <p className="flex h-[28px] items-center justify-center rounded-full bg-primary px-2 text-xs font-medium text-white sm:h-[32px] sm:px-3 sm:text-sm lg:h-[34px]">
              {reference.year}
            </p>
          )}
          {reference?.categories && reference?.categories?.length > 0 && (
            <p className="flex h-[28px] items-center justify-center rounded-full bg-primary px-2 text-xs font-medium text-white sm:h-[32px] sm:px-3 sm:text-sm lg:h-[34px]">
              {reference?.categories[0]?.name}
            </p>
          )}
        </div>
        <div className="relative z-10 flex w-full flex-col items-start justify-start gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col items-start justify-start gap-2 sm:gap-3">
            <Heading title={reference.name} color="white" />
            <Paragraph color="white" size="base" innerHtml={reference.description} />
          </div>
          {allImages?.length > 1 && (
            <Button variant={'white'} onClick={handleOpenGallery(allImages, 0)}>
              {t('text2367')} <LuArrowUpRight size={20} />
            </Button>
          )}
        </div>
        <div
          className={cn('absolute inset-0 z-[1] h-full w-full', 'ea-reference-card__image-overlay')}
        />
        <Image
          src={reference?.image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(reference?.image, reference?.name)}
          fill
          sizes="(min-width: 1280px) 920px, (min-width: 1024px) 70vw, 80vw"
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
        />
      </div>
    </>
  );
}
